import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { DatatableBadge, DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';
import { MigrationsAssessmentsContext } from '../../../services/migrations-assessments.context';
import { TranslateService } from '@ngx-translate/core';
import { RingCentralCallQueueReportEntry } from '../../../../shared/models/generated/ziroModels';
import { RingCentralCallQueuesContext } from '../ring-central-call-queues.context';
import { TelephoneNumberFilter } from '../../../../shared/filters/telephone-number.filter';
import { AudioFileResource } from '../../../../shared/resources/audio-file.resource';

interface CallQueueMemberRow extends DatatableRow {
  selectionOrder: number;
  name: string;
  extension: string;
  phoneNumbers: string;
}

@Component({
  selector: 'ziro-ring-central-call-queue',
  templateUrl: './ring-central-call-queue.component.html',
  styleUrls: ['./ring-central-call-queue.component.scss'],
  standalone: false,
})
export class RingCentralCallQueueComponent implements OnInit, OnDestroy {
  private _ringCentralCallQueueContext = inject(RingCentralCallQueuesContext);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _bottomNavService = inject(BottomNavService);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _translateService = inject(TranslateService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _audioFileResource = inject(AudioFileResource);

  ziroIcons = ZiroIcons;
  tenantId: string;
  exportId: string;
  isLoading = true;
  callQueue: RingCentralCallQueueReportEntry;
  directNumbers: string;
  memberColumns: DatatableColumn<CallQueueMemberRow>[] = [];
  memberRows: CallQueueMemberRow[] = [];

  greetingAudioUrl: string;
  greetingDownloadName: string;
  musicOnHoldAudioUrl: string;
  musicOnHoldDownloadName: string;

  private _exportName: string;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    let callQueueId: string;
    ({ tenantId: this.tenantId, id: this.exportId, callQueueId } = this._route.snapshot.params);

    this._initBottomNav();

    const sub = combineLatest([
      this._migrationsAssessmentsContext.state$,
      this._ringCentralCallQueueContext.state$,
    ]).subscribe(([allExports, callQueues]) => {
      this._exportName = allExports.ringCentralExports.find(
        (ringExport) => ringExport.exportId === this.exportId,
      )?.name;
      this.callQueue = callQueues.find((queue) => queue.id === callQueueId);
      this.directNumbers = this.callQueue.callQueueDirectNumbers
        .map((directNumber) => this._telephoneNumberFilter.transform(directNumber.number))
        .join(', ');
      this._buildMemberTable();
      this._initBreadcrumbs();
      this._fetchAudio();
      this.isLoading = false;
    });
    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  private _fetchAudio(): void {
    const greetingFilename = this.callQueue.audioInformation.greetingFilename;
    if (greetingFilename) {
      const greetingSub = this._audioFileResource
        .getAudioFile(this.tenantId, 'ring-central', greetingFilename, this.exportId)
        .subscribe((file) => {
          this.greetingAudioUrl = URL.createObjectURL(file);
          this.greetingDownloadName = file.name;
        });
      this._subscriptions.add(greetingSub);
    }

    const musicOnHoldFilename = this.callQueue.audioInformation.musicOnHoldFilename;
    if (musicOnHoldFilename) {
      const musicOnHoldSub = this._audioFileResource
        .getAudioFile(this.tenantId, 'ring-central', musicOnHoldFilename, this.exportId)
        .subscribe((file) => {
          this.musicOnHoldAudioUrl = URL.createObjectURL(file);
          this.musicOnHoldDownloadName = file.name;
        });
      this._subscriptions.add(musicOnHoldSub);
    }
  }

  private _buildMemberTable(): void {
    this.memberColumns = [
      {
        name: 'selectionOrder',
        label: 'tkey;migrations.ring_central.call_queues.detail.members.table.selection_order.label',
        hideColumn: this.callQueue.routingMethod !== 'FixedOrder',
        columnSize: 'col-sm-2',
      },
      { name: 'name', label: 'tkey;migrations.ring_central.call_queues.detail.members.table.name.label' },
      { name: 'extension', label: 'tkey;migrations.ring_central.call_queues.detail.members.table.extension.label' },
      {
        name: 'phoneNumbers',
        label: 'tkey;migrations.ring_central.call_queues.detail.members.table.phone_numbers.label',
        columnCharacterWidth: 25,
      },
    ];
    const phoneNumbersColumnCharacterWidth = this.memberColumns.find(
      (col) => col.name === 'phoneNumbers',
    ).columnCharacterWidth;
    this.memberRows = this.callQueue.callQueueMembership
      .map((member) => ({
        selectionOrder: member.selectionOrder,
        name: [member.associatedUser?.firstName, member.associatedUser?.lastName].filter(Boolean).join(' '),
        extension: member.number,
        phoneNumbers: member.callQueueMemberDirectNumbers
          .map((dn) => this._telephoneNumberFilter.transform(dn.number))
          .join(', '),
        html: {
          selectionOrder: `<span class="fa-stack"><i class="${ZiroIcons.CIRCLE} fa-stack-2x"></i><strong class="fa-stack-1x fa-inverse">${member.selectionOrder}</strong></span>`,
        },
        badges: {
          phoneNumbers: {
            values: this._buildBadges(
              member.callQueueMemberDirectNumbers.map((dn) => this._telephoneNumberFilter.transform(dn.number)),
              phoneNumbersColumnCharacterWidth,
            ),
          },
        },
      }))
      .sort((memberA, memberB) => memberA.selectionOrder - memberB.selectionOrder);
  }

  private _buildBadges(phoneNumbers: string[], maxChars: number): DatatableBadge<CallQueueMemberRow>[] {
    if (!phoneNumbers.length) {
      return [];
    }

    const totalChars = phoneNumbers.reduce((sum, phoneNumber) => sum + phoneNumber.length, 0);
    if (totalChars <= maxChars) {
      return phoneNumbers.map((phoneNumber) => ({ label: phoneNumber }));
    }

    const moreTkey = this._translateService.instant('tkey;datatable.badge.more.label');
    const visibleCount = Math.min(2, phoneNumbers.length);
    const badges: DatatableBadge<CallQueueMemberRow>[] = phoneNumbers.slice(0, visibleCount).map((phoneNumber) => ({
      label: phoneNumber,
      showTooltipOnTruncate: true,
    }));

    const remainingCount = phoneNumbers.length - visibleCount;
    if (remainingCount > 0) {
      badges.push({
        label: `${remainingCount}${moreTkey}`,
        hardcodedTooltip: this._buildBadgeTooltipMarkup(phoneNumbers.slice(visibleCount)),
      });
    }

    return badges;
  }

  private _buildBadgeTooltipMarkup(phoneNumbers: string[]): string {
    const formattedPhoneNumbers = phoneNumbers.map((phoneNumber) => `<li>${phoneNumber}</li>`).join('');
    return `<ul class="text-start mb-0 p-0">${formattedPhoneNumbers}</ul>`;
  }

  getRoutingMethodTkey(routingMethod: string): string {
    return this._ringCentralCallQueueContext.getRoutingMethodTkey(routingMethod);
  }

  getOverflowActionTkey(action: string): string {
    return this._ringCentralCallQueueContext.getOverflowActionTkey(action);
  }

  getTimeoutActionTkey(action: string): string {
    return this._ringCentralCallQueueContext.getTimeoutActionTkey(action);
  }

  getNoAgentsActionTkey(action: string): string {
    return this._ringCentralCallQueueContext.getNoAgentsActionTkey(action);
  }

  private _initBreadcrumbs(): void {
    if (!this.callQueue || !this._exportName) {
      return;
    }
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this.tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: this._exportName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this.tenantId}/ring-central/${this.exportId}`,
      },
      {
        label: 'tkey;migrations.ring_central.call_queues.title',
        routerLink: true,
        url: `/migrations/tenants/${this.tenantId}/ring-central/${this.exportId}/call-queues`,
      },
      { label: this.callQueue.name },
    ]);
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'ring-central-call-queue-back-button',
          dataAutomation: 'ring-central-call-queue-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => {
            this._router.navigate(['..'], { relativeTo: this._route });
          },
        },
      ]),
    );
  }
}
